import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Card, CardContent } from '../ui/card';
import {
  Search, CheckCircle2, AlertTriangle, Loader2, Check, X,
  Filter, Zap, Type, PenTool, Users, MapPin, Clock,
  RefreshCw, FileText, BookOpen, Eye, ChevronDown, ChevronRight,
  Shield, Tag, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../lib/api';

const CATEGORY_META = {
  spelling:     { icon: Type,       label: 'Spelling',     color: 'text-blue-400',    bg: 'bg-blue-500/10' },
  punctuation:  { icon: PenTool,    label: 'Punctuation',  color: 'text-cyan-400',    bg: 'bg-cyan-500/10' },
  dialogue:     { icon: FileText,   label: 'Dialogue',     color: 'text-pink-400',    bg: 'bg-pink-500/10' },
  character:    { icon: Users,      label: 'Character',    color: 'text-violet-400',  bg: 'bg-violet-500/10' },
  location:     { icon: MapPin,     label: 'Location',     color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  timeline:     { icon: Clock,      label: 'Timeline',     color: 'text-orange-400',  bg: 'bg-orange-500/10' },
  object:       { icon: BookOpen,   label: 'Object',       color: 'text-teal-400',    bg: 'bg-teal-500/10' },
  terminology:  { icon: Tag,        label: 'Terminology',  color: 'text-lime-400',    bg: 'bg-lime-500/10' },
  repetition:   { icon: RefreshCw,  label: 'Repetition',   color: 'text-amber-400',   bg: 'bg-amber-500/10' },
  style:        { icon: Sparkles,   label: 'Style',        color: 'text-indigo-400',  bg: 'bg-indigo-500/10' },
  readability:  { icon: Eye,        label: 'Readability',  color: 'text-rose-400',    bg: 'bg-rose-500/10' },
};

const GROUP_META = {
  mechanical:  { label: 'Mechanical',   color: 'text-blue-300',   bg: 'bg-blue-500/10',    icon: PenTool },
  consistency: { label: 'Consistency',  color: 'text-violet-300', bg: 'bg-violet-500/10',   icon: Users },
  style:       { label: 'Style',        color: 'text-amber-300',  bg: 'bg-amber-500/10',    icon: Sparkles },
};

const FindFixModal = ({ open, onOpenChange, project, user, onProjectUpdate }) => {
  const [step, setStep] = useState('config');
  const [scanning, setScanning] = useState(false);
  const [issues, setIssues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [grouped, setGrouped] = useState({});
  const [filter, setFilter] = useState({ group: null, category: null, status: 'pending' });
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [applying, setApplying] = useState(false);
  const [scope, setScope] = useState('manuscript');
  const [chapterNum, setChapterNum] = useState(1);
  const [mode, setMode] = useState('clean');
  const [sensitivity, setSensitivity] = useState('balanced');

  const scan = useCallback(async () => {
    setScanning(true);
    setStep('scanning');
    try {
      const res = await axios.post(`${API}/find-fix/scan`, {
        project_id: project.id,
        user_id: user.id,
        scope,
        chapter_number: scope === 'chapter' ? chapterNum : 0,
        mode,
        sensitivity,
        genre: project.genre || 'commercial_fiction',
      });
      setIssues(res.data.issues || []);
      setSummary(res.data.summary || {});

      // Load grouped issues
      const groupRes = await axios.get(`${API}/find-fix/issues/${project.id}?limit=500`);
      setGrouped(groupRes.data.grouped || {});

      setStep('results');
      const total = res.data.total_issues || 0;
      toast.success(`Scan complete: ${total} issue${total !== 1 ? 's' : ''} found`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Scan failed';
      toast.error(`Scan error: ${detail}`);
      setStep('config');
    }
    setScanning(false);
  }, [project, user, scope, chapterNum, mode, sensitivity]);

  const resolveIssue = async (issueId, action, customText = '') => {
    try {
      const params = new URLSearchParams({ issue_id: issueId, action });
      if (customText) params.append('custom_text', customText);
      const res = await axios.post(`${API}/find-fix/resolve?${params.toString()}`);
      if (res.data.success) {
        setIssues(prev => prev.map(i => i.issue_id === issueId
          ? { ...i, status: action === 'approve' ? 'fixed' : action === 'reject' ? 'rejected' : 'ignored' } : i));
        toast.success(action === 'approve' ? 'Fix applied' : action === 'reject' ? 'Dismissed' : 'Ignored');
        if (action === 'approve' && onProjectUpdate) onProjectUpdate();
      } else {
        toast.error(res.data.message || 'Action failed');
      }
    } catch (e) { toast.error('Failed to resolve'); }
    setExpandedIssue(null);
  };

  const applyAlternative = async (issueId, altText) => {
    try {
      const params = new URLSearchParams({ issue_id: issueId, action: 'custom', custom_text: altText });
      const res = await axios.post(`${API}/find-fix/resolve?${params.toString()}`);
      if (res.data.success) {
        setIssues(prev => prev.map(i => i.issue_id === issueId ? { ...i, status: 'fixed' } : i));
        toast.success('Alternative applied');
        if (onProjectUpdate) onProjectUpdate();
      }
    } catch (e) { toast.error('Failed'); }
    setExpandedIssue(null);
  };

  const ignorePattern = async (pattern) => {
    try {
      await axios.post(`${API}/find-fix/ignore-pattern`, {
        project_id: project.id, user_id: user.id, pattern, scope: 'project',
      });
      setIssues(prev => prev.map(i => i.source_span === pattern ? { ...i, status: 'ignored' } : i));
      toast.success('Pattern will be ignored in future scans');
    } catch (e) { toast.error('Failed'); }
  };

  const applySafe = async () => {
    setApplying(true);
    try {
      const res = await axios.post(`${API}/find-fix/apply-safe`, { project_id: project.id });
      toast.success(res.data.message);
      if (res.data.fixed > 0 && onProjectUpdate) onProjectUpdate();
      await refreshIssues();
    } catch (e) { toast.error('Failed'); }
    setApplying(false);
  };

  const applyAllFixable = async () => {
    setApplying(true);
    try {
      const res = await axios.post(`${API}/find-fix/apply-all-fixable`, { project_id: project.id });
      toast.success(res.data.message);
      if (res.data.fixed > 0 && onProjectUpdate) onProjectUpdate();
      await refreshIssues();
    } catch (e) { toast.error('Failed'); }
    setApplying(false);
  };

  const refreshIssues = async () => {
    const res = await axios.get(`${API}/find-fix/issues/${project.id}?limit=500`);
    setIssues(res.data.issues || []);
    setGrouped(res.data.grouped || {});
  };

  const filteredIssues = issues.filter(i => {
    if (filter.status && i.status !== filter.status) return false;
    if (filter.category && i.category !== filter.category) return false;
    if (filter.group) {
      const mechanical = ['spelling', 'punctuation', 'dialogue'];
      const consistency = ['character', 'location', 'timeline', 'object', 'terminology'];
      if (filter.group === 'mechanical' && !mechanical.includes(i.category)) return false;
      if (filter.group === 'consistency' && !consistency.includes(i.category)) return false;
      if (filter.group === 'style' && (mechanical.includes(i.category) || consistency.includes(i.category))) return false;
    }
    return true;
  });

  const pendingCount = issues.filter(i => i.status === 'pending').length;
  const safeCount = issues.filter(i => i.status === 'pending' && i.classification === 'safe_auto_fix').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl bg-slate-900 border-slate-700 text-white" data-testid="find-fix-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Search className="w-5 h-5 text-rose-400" />
            Issue Scanner
            <Badge variant="outline" className="text-rose-400 border-rose-500/50 text-xs ml-2">LOCAL</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* CONFIG SCREEN */}
        {step === 'config' && (
          <div className="space-y-4 py-2">
            <p className="text-xs text-slate-400">Scan your manuscript for spelling, punctuation, name drift, consistency, and style issues. No API calls.</p>

            <div className="grid grid-cols-2 gap-3">
              {/* Scope */}
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Scan Scope</p>
                <div className="flex flex-col gap-1.5">
                  {[
                    { key: 'manuscript', label: 'Entire Manuscript' },
                    { key: 'chapter', label: 'Single Chapter' },
                  ].map(s => (
                    <button key={s.key} onClick={() => setScope(s.key)} data-testid={`scope-${s.key}`}
                      className={`py-1.5 px-3 rounded text-xs text-left font-medium transition-all ${scope === s.key ? 'bg-rose-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                {scope === 'chapter' && (
                  <select value={chapterNum} onChange={(e) => setChapterNum(parseInt(e.target.value))}
                    className="mt-2 w-full rounded bg-slate-800 border border-slate-700 text-slate-200 px-2 py-1.5 text-xs"
                    data-testid="find-fix-chapter-select">
                    {(project?.chapter_outline && project.chapter_outline.length > 0
                      ? project.chapter_outline.map(ch => (
                          <option key={ch.number || ch.chapter_number} value={ch.number || ch.chapter_number}>
                            Ch {ch.number || ch.chapter_number}: {ch.title || 'Untitled'}
                          </option>
                        ))
                      : // Fallback: extract chapter numbers from content
                        (project?.content?.match(/---\s*CHAPTER\s+(\d+)/gi) || []).map((m, idx) => {
                          const num = parseInt(m.match(/\d+/)?.[0]) || idx + 1;
                          return <option key={num} value={num}>Chapter {num}</option>;
                        })
                    )}
                    {(!project?.chapter_outline?.length && !(project?.content?.match(/---\s*CHAPTER\s+\d+/gi) || []).length) && (
                      <option value={1}>Chapter 1 (auto)</option>
                    )}
                  </select>
                )}
              </div>

              {/* Mode */}
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Detection Mode</p>
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => setMode('clean')} data-testid="ff-mode-clean"
                    className={`py-1.5 px-3 rounded text-xs text-left font-medium transition-all ${mode === 'clean' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                    Clean (Mechanical + Continuity)
                  </button>
                  <button onClick={() => setMode('polish')} data-testid="ff-mode-polish"
                    className={`py-1.5 px-3 rounded text-xs text-left font-medium transition-all ${mode === 'polish' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                    Polish (+ Style + Readability)
                  </button>
                </div>

                <p className="text-[10px] text-slate-500 mt-3 mb-1">Sensitivity</p>
                <div className="flex gap-1">
                  {['conservative', 'balanced', 'aggressive'].map(s => (
                    <button key={s} onClick={() => setSensitivity(s)} data-testid={`ff-sens-${s}`}
                      className={`flex-1 py-1 px-2 rounded text-[10px] font-medium transition-all ${sensitivity === s ? 'bg-cyan-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-center pt-2">
              <Button onClick={scan} className="bg-rose-600 hover:bg-rose-500 px-8" data-testid="start-scan-btn">
                <Search className="w-4 h-4 mr-2" /> Scan Now
              </Button>
            </div>
          </div>
        )}

        {/* SCANNING */}
        {step === 'scanning' && (
          <div className="py-10 text-center">
            <Loader2 className="w-10 h-10 text-rose-400 mb-3 animate-spin" />
            <p className="text-white font-medium">Scanning {scope === 'chapter' ? `Chapter ${chapterNum}` : 'manuscript'}...</p>
            <p className="text-xs text-slate-400 mt-1">Running local detectors (no API calls)</p>
          </div>
        )}

        {/* RESULTS */}
        {step === 'results' && (
          <div className="space-y-3">
            {/* Group summary cards */}
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(GROUP_META).map(([key, meta]) => {
                const count = summary?.by_group?.[key] || 0;
                const Icon = meta.icon;
                return (
                  <button key={key} onClick={() => setFilter(f => ({ ...f, group: f.group === key ? null : key, category: null }))}
                    className={`rounded-lg p-2.5 text-left transition-all border ${filter.group === key ? `${meta.bg} border-current ${meta.color}` : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'}`}
                    data-testid={`group-${key}`}>
                    <div className="flex items-center gap-1.5">
                      <Icon className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">{meta.label}</span>
                    </div>
                    <p className="text-lg font-bold mt-0.5">{count}</p>
                  </button>
                );
              })}
            </div>

            {/* Action bar */}
            <div className="flex items-center gap-2 flex-wrap">
              {safeCount > 0 && (
                <Button size="sm" onClick={applySafe} disabled={applying}
                  className="bg-emerald-600 hover:bg-emerald-500 text-xs h-7" data-testid="apply-safe-btn">
                  {applying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Shield className="w-3 h-3 mr-1" />}
                  Apply {safeCount} Safe Fixes
                </Button>
              )}
              {pendingCount > safeCount && (
                <Button size="sm" variant="outline" onClick={applyAllFixable} disabled={applying}
                  className="border-slate-600 text-xs h-7" data-testid="apply-all-btn">
                  <Zap className="w-3 h-3 mr-1" /> Apply All Fixable
                </Button>
              )}
              <div className="flex-1" />
              <Button size="sm" variant="ghost" onClick={() => setStep('config')} className="text-slate-400 text-xs h-7">
                <RefreshCw className="w-3 h-3 mr-1" /> Re-scan
              </Button>
            </div>

            {/* Filter bar */}
            <div className="flex items-center gap-1 flex-wrap">
              <Filter className="w-3 h-3 text-slate-500" />
              {['pending', 'fixed', 'rejected', 'ignored'].map(s => (
                <Button key={s} size="sm" variant={filter.status === s ? 'default' : 'ghost'}
                  onClick={() => setFilter(f => ({ ...f, status: f.status === s ? null : s }))}
                  className={`text-[10px] h-6 px-2 ${filter.status === s ? 'bg-slate-700' : 'text-slate-500'}`}>
                  {s} ({issues.filter(i => i.status === s).length})
                </Button>
              ))}
              <div className="w-px h-4 bg-slate-700 mx-0.5" />
              {Object.entries(CATEGORY_META).map(([key, cfg]) => {
                const count = issues.filter(i => i.category === key && (!filter.status || i.status === filter.status)).length;
                if (!count) return null;
                const Icon = cfg.icon;
                return (
                  <Button key={key} size="sm" variant={filter.category === key ? 'default' : 'ghost'}
                    onClick={() => setFilter(f => ({ ...f, category: f.category === key ? null : key }))}
                    className={`text-[10px] h-6 px-2 ${filter.category === key ? 'bg-slate-700' : 'text-slate-500'}`}>
                    <Icon className="w-3 h-3 mr-0.5" />{count}
                  </Button>
                );
              })}
            </div>

            {/* Issues list */}
            <div className="max-h-[350px] overflow-y-auto space-y-1.5 pr-1" data-testid="find-fix-issues-list">
              {filteredIssues.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" />
                  <p className="text-white font-medium text-sm">All Clear</p>
                  <p className="text-xs text-slate-400">No issues matching this filter</p>
                </div>
              ) : (
                filteredIssues.map(issue => (
                  <FindFixIssueRow key={issue.issue_id} issue={issue}
                    expanded={expandedIssue === issue.issue_id}
                    onToggle={() => setExpandedIssue(expandedIssue === issue.issue_id ? null : issue.issue_id)}
                    onResolve={resolveIssue}
                    onAlternative={applyAlternative}
                    onIgnorePattern={ignorePattern} />
                ))
              )}
            </div>

            {pendingCount === 0 && (
              <Card className="bg-emerald-500/10 border-emerald-500/30">
                <CardContent className="p-3 text-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 mb-1" />
                  <p className="text-emerald-300 font-semibold text-sm">All Issues Resolved</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FindFixIssueRow = ({ issue, expanded, onToggle, onResolve, onAlternative, onIgnorePattern }) => {
  const cat = CATEGORY_META[issue.category] || CATEGORY_META.spelling;
  const Icon = cat.icon;
  const isResolved = issue.status !== 'pending';

  const sevColors = {
    critical: 'border-red-500/40 bg-red-500/10',
    moderate: 'border-orange-500/40 bg-orange-500/10',
    minor: 'border-yellow-500/30 bg-yellow-500/5',
    informational: 'border-slate-700 bg-slate-800/30',
  };

  return (
    <div className={`border rounded-lg transition-all ${isResolved ? 'border-slate-800 opacity-50' : sevColors[issue.severity] || sevColors.minor}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-2 p-2.5 text-left" data-testid={`ff-issue-${issue.issue_id}`}>
        <Icon className={`w-4 h-4 flex-shrink-0 ${cat.color}`} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white truncate">{issue.message}</p>
          <p className="text-[10px] text-slate-500">Ch {issue.chapter_number} | {issue.reason_label || issue.category} | Tier {issue.tier}</p>
        </div>
        <Badge className={`text-[10px] border-0 ${cat.bg} ${cat.color}`}>{cat.label}</Badge>
        {isResolved && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border-0">{issue.status}</Badge>}
        {expanded ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-700/50">
          {issue.source_span && (
            <div className="mt-2">
              <p className="text-[10px] text-slate-500 mb-1">Found:</p>
              <code className="text-xs text-red-300 bg-red-500/10 px-2 py-1 rounded block break-all">{issue.source_span}</code>
            </div>
          )}
          {issue.suggested_fix && (
            <div>
              <p className="text-[10px] text-slate-500 mb-1">Suggested:</p>
              <code className="text-xs text-emerald-300 bg-emerald-500/10 px-2 py-1 rounded block break-all">{issue.suggested_fix}</code>
            </div>
          )}
          {issue.alternate_options?.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              <span className="text-[10px] text-slate-500">Alternatives:</span>
              {issue.alternate_options.map((alt, i) => (
                <Badge key={i} className="text-[10px] bg-slate-700 text-slate-300 cursor-pointer hover:bg-slate-600"
                  onClick={() => onAlternative(issue.issue_id, alt)}>{alt}</Badge>
              ))}
            </div>
          )}
          {!isResolved && (
            <div className="flex items-center gap-2 pt-1">
              {issue.suggested_fix && (
                <Button size="sm" onClick={() => onResolve(issue.issue_id, 'approve')}
                  className="bg-emerald-600 hover:bg-emerald-500 text-xs h-7" data-testid={`ff-approve-${issue.issue_id}`}>
                  <Check className="w-3 h-3 mr-1" /> Apply
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onResolve(issue.issue_id, 'reject')}
                className="text-slate-400 text-xs h-7">
                <X className="w-3 h-3 mr-1" /> Dismiss
              </Button>
              {issue.source_span && (
                <Button size="sm" variant="ghost" onClick={() => onIgnorePattern(issue.source_span)}
                  className="text-slate-500 text-xs h-7">
                  <Eye className="w-3 h-3 mr-1" /> Always Ignore
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FindFixModal;
